import React, { useRef, useEffect } from 'react';
import styled from '@emotion/styled';

import { useAppSelector, useAppDispatch } from '../redux';
import { PageContainer } from '../components/pageContainer';
import { Header } from '../components/header';
import { SubjectRowLabel, RowDetails } from '../components/StyledComponents';

import { primary } from '../utils/colors';
import { LeftNav } from '../components/leftNav';
import { Text } from '../components/text';
import { ActionBar } from '../components/styles';
import { setState } from '../redux/actions/user';

export const TutorialVideo: React.FC = () => {
  const dashboard = useAppSelector((state) => state.user.dashboard);
  const dispatch = useAppDispatch();
  const debugPrint = false;

  const tutorialRef = useRef<HTMLDetailsElement>(null);
  const studentDashboardRef = useRef<HTMLDetailsElement>(null);
  const secondTutorialRef = useRef<HTMLDetailsElement>(null);

  // Set initial open state based on whether videos have been viewed
  useEffect(() => {
    if (tutorialRef.current && !dashboard?.tutorialVideoViewed) {
      tutorialRef.current.open = true;
    }
    if (studentDashboardRef.current && !dashboard?.studentDashboardVideoViewed) {
      studentDashboardRef.current.open = true;
    }
    if (secondTutorialRef.current && !dashboard?.secondTutorialVideoViewed) {
      secondTutorialRef.current.open = true;
    }
  }, []);

  const hoverTutorialVideo = () => {
    if (!dashboard?.tutorialVideoViewed) {
      if (debugPrint) console.log('setting tutorialVideoViewed');
      dispatch(setState({ ...dashboard, tutorialVideoViewed: true }));
    } else {
      if (debugPrint) console.log('hover tutorial video');
    }
  };

  const hoverStudentDashboardVideo = () => {
    if (!dashboard?.studentDashboardVideoViewed) {
      if (debugPrint) console.log('setting studentDashboardVideoViewed');
      dispatch(setState({ ...dashboard, studentDashboardVideoViewed: true }));
    } else {
      if (debugPrint) console.log('hover student dashboard video');
    }
  };

  const hoverSecondTutorialVideo = () => {
    if (!dashboard?.secondTutorialVideoViewed) {
      if (debugPrint) console.log('setting secondTutorialVideoViewed');
      dispatch(setState({ ...dashboard, secondTutorialVideoViewed: true }));
    } else {
      if (debugPrint) console.log('hover second tutorial video');
    }
  };

  return (
    <PageContainer header={<Header />} leftNav={<LeftNav />}>
      <ActionBar data-cy="tutorial-video-page">
        <Text variant="lg2" color={primary}>
          Dashboard Overviews
        </Text>
      </ActionBar>
      <RowDetails ref={tutorialRef}>
        <SubjectRowLabel style={{ cursor: 'pointer' }}>
          Should I Use the Original or New Teacher Dashboard?
        </SubjectRowLabel>
        <StyledText variant="md">
          <span>
            The Prisms Teacher Dashboard allows educators to monitor student progress in VR and provide just-in-time
            feedback and support. Use the dashboard that corresponds to the app for each module: the Original Dashboard
            is compatible with the Pink Math and Orange Science apps, while the New Dashboard supports the Blue Math and
            Green Science apps. If you need additional assistance with the Dashboard, please submit a support
            ticket{' '}
          </span>
          <StyledAnchor href="https://share.hsforms.com/1ItTGWzdXRk-l-WTou7feYwbwtrt" target="_blank">
            here
          </StyledAnchor>
          <span>.</span>
        </StyledText>
        <StyledVideo
          width="560"
          height="auto"
          src="https://assets.prismsvr.com/teacher-dashboard/how-to-use-the-teacher-dashboard.mp4"
          controls
          onMouseEnter={hoverTutorialVideo}
        ></StyledVideo>
      </RowDetails>
      <RowDetails ref={studentDashboardRef}>
        <SubjectRowLabel style={{ cursor: 'pointer' }}>
          How to Administer Check for Understanding Assessments
        </SubjectRowLabel>
        <StyledText variant="md">
          The Student Dashboard provides access to Check for Understanding (CFU) assessments. This video demonstrates
          how students complete CFUs and how teachers can view and interpret their data.
        </StyledText>
        <StyledVideo
          width="560"
          height="auto"
          src="https://assets.prismsvr.com/teacher-dashboard/administer-checks-of-understanding-assessments.mp4"
          controls
          onMouseEnter={hoverStudentDashboardVideo}
        ></StyledVideo>
      </RowDetails>
      <RowDetails ref={secondTutorialRef}>
        <SubjectRowLabel style={{ cursor: 'pointer' }}>Accessing and Using the New Teacher Dashboard</SubjectRowLabel>
        <StyledText variant="md">
          The New Teacher Dashboard includes all the features of the Original Teacher Dashboard, plus several new and
          enhanced capabilities. This video walks through how to access and use these features effectively.
        </StyledText>
        <StyledVideo
          width="560"
          height="auto"
          src="https://assets.prismsvr.com/teacher-dashboard/features-deeper-dive.mp4"
          controls
          onMouseEnter={hoverSecondTutorialVideo}
        ></StyledVideo>
      </RowDetails>
    </PageContainer>
  );
};

const StyledVideo = styled.video({
  margin: '1rem auto 1rem',
  display: 'block',
});

const StyledText = styled(Text)({
  margin: '1rem 3rem 1rem 3rem',
  display: 'block',
});

const StyledAnchor = styled.a({
  color: primary,
  textDecoration: 'none',
});
